import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../api';
import './AvatarGallery.css';
import { useToast } from '../UI/ToastContext';

const AvatarGallery = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [imageCache, setImageCache] = useState({});
  const [loadingImages, setLoadingImages] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [styles, setStyles] = useState({}); // Pour stocker les informations des styles
  
  // Référence pour suivre les tentatives de chargement d'images
  const loadAttempts = useRef({});
  
  // Hook pour utiliser les toasts
  const { showError } = useToast();
  
  // Utiliser useCallback pour éviter de recréer ces fonctions à chaque rendu
  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/aivatar/user-tasks');
      if (response.data && response.data.tasks) {
        setTasks(response.data.tasks);
      }
      setError(null);
    } catch (err) {
      console.error('Error loading avatars:', err);
      setError('Unable to load your avatars. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Récupérer les informations de style depuis l'API
  const fetchStyles = useCallback(async () => {
    try {
      const response = await api.get('/aivatar/styles');
      if (response.data && response.data.success && response.data.styles) {
        // Créer un map des styles pour un accès facile par ID
        const stylesMap = {};
        response.data.styles.forEach(style => {
          stylesMap[style.id] = style;
        });
        setStyles(stylesMap);
      }
    } catch (err) {
      console.error('Error loading styles:', err);
    }
  }, []);

  // Charge l'image de résultat pour une tâche terminée
  const loadResultImage = useCallback(async (task) => {
    if (!task.result_path || 
        imageCache[task.result_path] || 
        loadingImages[task.result_path] ||
        (loadAttempts.current[task.result_path] && loadAttempts.current[task.result_path] >= 2)) {
      return; // L'image est déjà en cache, en cours de chargement, ou a échoué trop de fois
    }

    // Incrémenter le compteur de tentatives
    loadAttempts.current[task.result_path] = (loadAttempts.current[task.result_path] || 0) + 1;

    try {
      setLoadingImages(prev => ({
        ...prev,
        [task.result_path]: true
      }));

      // Extraire le nom du fichier à partir du chemin
      const pathParts = task.result_path.split('/');
      const filename = pathParts[pathParts.length - 1];
      
      // Utiliser la nouvelle route qui renvoie des données en base64
      const response = await api.get(`/aivatar/user-file/data/results/${filename}`, {
        responseType: 'json'
      });
      
      if (response.data && response.data.success && response.data.data) {
        // Créer une URL à partir des données base64
        const imageUrl = `data:${response.data.content_type};base64,${response.data.data}`;
        
        // Stocker l'URL de l'image en cache
        setImageCache(prev => ({
          ...prev,
          [task.result_path]: imageUrl
        }));

        // Nettoyer les erreurs éventuelles
        setImageErrors(prev => {
          const newErrors = {...prev};
          delete newErrors[task.result_path];
          return newErrors;
        });
      }
      
    } catch (err) {
      console.error(`Error loading image for task ${task.id}:`, err);
      // Seulement définir une erreur si la tâche est terminée
      if (task.status === 'DONE') {
        setImageErrors(prev => ({
          ...prev,
          [task.result_path]: "Unable to load image"
        }));
      }
    } finally {
      setLoadingImages(prev => ({
        ...prev,
        [task.result_path]: false
      }));
    }
  }, [imageCache, loadingImages]);

  // Charge l'image de référence pour une tâche
  const loadReferenceImage = useCallback(async (task) => {
    if (!task.reference_path || 
        imageCache[task.reference_path] || 
        loadingImages[task.reference_path] ||
        (loadAttempts.current[task.reference_path] && loadAttempts.current[task.reference_path] >= 2)) {
      return; // L'image est déjà en cache, en cours de chargement, ou a échoué trop de fois
    }

    // Incrémenter le compteur de tentatives
    loadAttempts.current[task.reference_path] = (loadAttempts.current[task.reference_path] || 0) + 1;

    try {
      setLoadingImages(prev => ({
        ...prev,
        [task.reference_path]: true
      }));

      // Extraire le nom du fichier à partir du chemin
      const pathParts = task.reference_path.split('/');
      const filename = pathParts[pathParts.length - 1];
      
      // Déterminer l'ID utilisateur depuis le chemin
      const userIdMatch = task.reference_path.match(/user_(\d+)/);
      const userId = userIdMatch ? userIdMatch[1] : '';
      
      // Utiliser la route photos pour récupérer l'image de référence
      const response = await api.get(`/photos/data/${filename}`, {
        responseType: 'json'
      });
      
      if (response.data && response.data.success && response.data.data) {
        // Créer une URL à partir des données base64
        const imageUrl = `data:${response.data.content_type};base64,${response.data.data}`;
        
        // Stocker l'URL de l'image en cache
        setImageCache(prev => ({
          ...prev,
          [task.reference_path]: imageUrl
        }));

        // Nettoyer les erreurs éventuelles
        setImageErrors(prev => {
          const newErrors = {...prev};
          delete newErrors[task.reference_path];
          return newErrors;
        });
      }
      
    } catch (err) {
      console.error(`Error loading reference image for task ${task.id}:`, err);
      setImageErrors(prev => ({
        ...prev,
        [task.reference_path]: "Unable to load image"
      }));
    } finally {
      setLoadingImages(prev => ({
        ...prev,
        [task.reference_path]: false
      }));
    }
  }, [imageCache, loadingImages]);
  
  // Charger les images une fois que les tâches sont chargées
  useEffect(() => {
    if (tasks.length > 0) {
      // Pour chaque tâche terminée, charger l'image de résultat
      tasks.forEach(task => {
        if (task.status === 'DONE' && task.result_path) {
          loadResultImage(task);
        }
        // Pour toutes les tâches, charger l'image de référence
        if (task.reference_path) {
          loadReferenceImage(task);
        }
      });
    }
  }, [tasks, loadResultImage, loadReferenceImage]);

  // Charger les styles au démarrage
  useEffect(() => {
    fetchStyles();
  }, [fetchStyles]);

  // Effet pour charger les tâches et configurer l'intervalle de rafraîchissement
  useEffect(() => {
    // Charger les tâches initialement
    fetchTasks();
    
    // Rafraîchir les tâches en cours toutes les 15 secondes
    let interval = null;
    
    const checkForActiveTasks = () => {
      if (tasks.some(task => task.status === 'TODO' || task.status === 'IN_PROGRESS')) {
        return true;
      }
      return false;
    };
    
    const startPolling = () => {
      if (interval) clearInterval(interval);
      interval = setInterval(() => {
        if (checkForActiveTasks()) {
          fetchTasks();
        } else {
          clearInterval(interval);
        }
      }, 15000);
    };
    
    if (tasks.length > 0 && checkForActiveTasks()) {
      startPolling();
    }
    
    // Nettoyer l'intervalle quand le composant est démonté
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchTasks, refreshCounter]); // Ne dépend que de fetchTasks et refreshCounter

  // Récupère le nom du style à partir de l'ID
  const getStyleName = (styleId) => {
    if (styles[styleId]) {
      return styles[styleId].name;
    }
    return `Style ${styleId}`;
  };

  const handleDownload = async (task, e) => {
    e.stopPropagation(); // Prevent modal opening
    
    if (!task.result_path) return;

    try {
      // Show loading state for download button
      setLoadingImages(prev => ({
        ...prev,
        [`download_${task.id}`]: true
      }));
      
      // Extract filename from path
      const pathParts = task.result_path.split('/');
      const filename = pathParts[pathParts.length - 1];
      
      console.log(`Attempting to download: ${filename}`);
      console.log(`Complete path: ${task.result_path}`);
      
      // Option 1: Try to diagnose first
      try {
        const diagResponse = await api.get('/aivatar/diagnose/directories');
        console.log("Diagnostic result:", diagResponse.data);
      } catch (diagError) {
        console.error("Error during diagnosis:", diagError);
      }
      
      // Option 2: Try base64 version first
      try {
        console.log("Attempting with base64 API");
        const b64Response = await api.get(`/aivatar/user-file/data/results/${filename}`);
        
        if (b64Response.data && b64Response.data.success && b64Response.data.data) {
          console.log("Successfully retrieved base64 data, creating blob");
          const binary = atob(b64Response.data.data);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
          }
          
          const blob = new Blob([bytes], { type: b64Response.data.content_type });
          const url = window.URL.createObjectURL(blob);
          
          // Create temporary <a> element to trigger download
          const link = document.createElement('a');
          link.href = url;
          link.style.display = 'none';
          
          // Determine file extension
          const extension = filename.substring(filename.lastIndexOf('.'));
          
          // Set download filename (prefer simpler name)
          const displayName = filename.includes('_') 
            ? filename.split('_')[0] + extension 
            : filename;
          
          link.setAttribute('download', displayName);
          document.body.appendChild(link);
          
          // Trigger download
          link.click();
          
          // Cleanup
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);
          }, 100);
          
          return; // Exit if base64 method worked
        }
      } catch (b64Error) {
        console.error("Failed to retrieve base64 data:", b64Error);
      }
      
      // Option 3: Try standard blob method
      console.log("Attempting with blob API");
      const response = await api.get(`/aivatar/user-file/results/${filename}`, {
        responseType: 'blob' // Important: specify blob response type
      });
      
      // Create URL from received blob
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      
      console.log(`Blob created successfully, type: ${response.headers['content-type']}, size: ${response.data.size}`);
      
      // Create temporary <a> element to trigger download
      const link = document.createElement('a');
      link.href = url;
      link.style.display = 'none';
      
      // Determine file extension
      const extension = filename.substring(filename.lastIndexOf('.'));
      
      // Set download filename (prefer simpler name)
      const displayName = filename.includes('_') 
        ? filename.split('_')[0] + extension 
        : filename;
      
      console.log(`Downloading file with name: ${displayName}`);
      
      link.setAttribute('download', displayName);
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      }, 100);
      
    } catch (err) {
      console.error("Error downloading image:", err);
      console.error("Error message:", err.message);
      if (err.response) {
        console.error("Response details:", err.response.data);
        console.error("Status:", err.response.status);
      }
      // Remplacer le message en français par un message en anglais
      showError("An error occurred while downloading the image. Check console for details.");
    } finally {
      // Remove loading state
      setLoadingImages(prev => {
        const newLoadingState = { ...prev };
        delete newLoadingState[`download_${task.id}`];
        return newLoadingState;
      });
    }
  };

  const openModal = (task) => {
    if (task.status === 'DONE' && task.result_path) {
      setSelectedAvatar(task);
      setShowModal(true);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedAvatar(null);
  };

  const refreshGallery = () => {
    // Réinitialiser le cache et les états
    setImageCache({});
    setLoadingImages({});
    setImageErrors({});
    loadAttempts.current = {}; // Réinitialiser les tentatives
    setRefreshCounter(prev => prev + 1); // Incrémenter pour déclencher un nouveau chargement
    fetchTasks(); // Charger immédiatement les tâches
    fetchStyles(); // Recharger également les styles
  };

  const renderTaskStatus = (task) => {
    switch (task.status) {
      case 'TODO':
        return (
          <div className="avatar-status pending">
            <div className="status-indicator"></div>
            <span>Pending</span>
          </div>
        );
      case 'IN_PROGRESS':
        return (
          <div className="avatar-status progress">
            <div className="loader"></div>
            <span>{task.progress || 0}%</span>
          </div>
        );
      case 'ERROR':
        return (
          <div className="avatar-status error">
            <div className="status-indicator"></div>
            <span>Error</span>
          </div>
        );
      default:
        return null;
    }
  };

  const getTaskDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const groupTasksByDate = () => {
    const grouped = {};

    tasks.forEach(task => {
      // Convert timestamp to date (day only)
      const date = new Date(task.created_at * 1000);
      const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      
      // Create group if it doesn't exist
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          dateLabel: date.toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          }),
          tasks: []
        };
      }
      
      // Add task to group
      grouped[dateKey].tasks.push(task);
    });

    // Sort groups by date (newest first)
    return Object.values(grouped).sort((a, b) => 
      new Date(b.tasks[0].created_at * 1000) - new Date(a.tasks[0].created_at * 1000)
    );
  };

  if (loading && tasks.length === 0) {
    return (
      <div className="avatar-gallery-container">
        <div className="gallery-loading">
          <div className="loader"></div>
          <p>Loading your avatars...</p>
        </div>
      </div>
    );
  }

  const groupedTasks = groupTasksByDate();

  return (
    <div className="avatar-gallery-container">
      <div className="gallery-header">
        <h1>My Avatars</h1>
        <button 
          className="refresh-button" 
          onClick={refreshGallery}
          title="Refresh gallery"
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 12C2 6.48 6.48 2 12 2C17.52 2 22 6.48 22 12C22 17.52 17.52 22 12 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M8 16H2V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M22 10L16 10L16 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
      
      {error && <div className="gallery-error">{error}</div>}
      
      {tasks.length === 0 ? (
        <div className="no-avatars">
          <p>You haven't created any avatars yet.</p>
          <p>Go to "Create Avatar" to generate one!</p>
        </div>
      ) : (
        <div className="avatar-groups">
          {groupedTasks.map((group, groupIndex) => (
            <div key={groupIndex} className="avatar-group">
              <h2 className="date-header">{group.dateLabel}</h2>
              <div className="avatars-grid">
                {group.tasks.map((task) => (
                  <div 
                    key={task.id} 
                    className={`avatar-card ${task.status.toLowerCase()}`}
                    onClick={() => openModal(task)}
                  >
                    <div className="avatar-image-container">
                      {task.status === 'DONE' ? (
                        <>
                          {loadingImages[task.result_path] && (
                            <div className="image-loading">
                              <div className="loader"></div>
                            </div>
                          )}
                          <img 
                            src={imageCache[task.result_path] || '/placeholder-image.svg'}
                            alt={`Avatar ${task.id}`}
                            className="avatar-image"
                            style={{opacity: loadingImages[task.result_path] ? 0 : 1}}
                            onError={(e) => {
                              // Ne définir une erreur que si la tâche est terminée
                              if (task.status === 'DONE') {
                                setImageErrors(prev => ({
                                  ...prev,
                                  [task.result_path]: "Unable to load image"
                                }));
                              }
                              e.target.src = '/placeholder-error.png';
                            }}
                          />
                          {imageErrors[task.result_path] && task.status === 'DONE' && (
                            <div className="image-error-overlay">
                              <i className="fas fa-exclamation-triangle"></i>
                              <span>{imageErrors[task.result_path]}</span>
                            </div>
                          )}
                          <button 
                            className="download-button" 
                            onClick={(e) => handleDownload(task, e)}
                            title="Download avatar"
                            disabled={loadingImages[task.result_path] || imageErrors[task.result_path]}
                          >
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 16L12 8M12 16L15 13M12 16L9 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M3 15V16C3 18.2091 4.79086 20 7 20H17C19.2091 20 21 18.2091 21 16V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </>
                      ) : (
                        <>
                          {loadingImages[task.reference_path] && (
                            <div className="image-loading">
                              <div className="loader"></div>
                            </div>
                          )}
                          <img 
                            src={imageCache[task.reference_path] || '/placeholder-image.svg'}
                            alt={`Reference ${task.id}`}
                            className="reference-image"
                            style={{opacity: loadingImages[task.reference_path] ? 0.7 : 0.7}}
                            onError={(e) => {
                              e.target.src = '/placeholder-error.png';
                            }}
                          />
                          {renderTaskStatus(task)}
                        </>
                      )}
                    </div>
                    <div className="avatar-info">
                      <div className="avatar-time">{getTaskDate(task.updated_at || task.created_at)}</div>
                      <div className="avatar-style">Style: {getStyleName(task.style_id)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal to view avatar in full size */}
      {showModal && selectedAvatar && (
        <div className="avatar-modal-overlay" onClick={closeModal}>
          <div className="avatar-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Generated Avatar</h2>
              <button className="close-button" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              {imageCache[selectedAvatar.result_path] ? (
                <img 
                  src={imageCache[selectedAvatar.result_path]}
                  alt={`Avatar ${selectedAvatar.id}`} 
                  className="modal-avatar-image"
                  onError={(e) => {
                    setImageErrors(prev => ({
                      ...prev,
                      [selectedAvatar.result_path]: "Unable to load image"
                    }));
                    e.target.src = '/placeholder-error.png';
                  }}
                />
              ) : (
                <div className="modal-image-loading">
                  <div className="loader"></div>
                  <p>Loading image...</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <div className="avatar-details">
                <p><strong>Style:</strong> {getStyleName(selectedAvatar.style_id)}</p>
                <p><strong>Created:</strong> {getTaskDate(selectedAvatar.created_at)}</p>
              </div>
              <button 
                className="btn btn-primary" 
                onClick={(e) => handleDownload(selectedAvatar, e)}
                disabled={!imageCache[selectedAvatar.result_path]}
              >
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvatarGallery; 